import { Button, Col, Modal, Row, Steps, Tabs, Tag, Tooltip, Typography } from "antd";
import { ReactNode, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { trainingAPI } from "@api/TrainingAPI";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import dayjs from "dayjs";
import FirstStep from "./FirstStep";
import SecondStep from "./SecondStep";
import ThirdStep from "./ThirdStep";
import FixedValuesTab from "./FixedValuesTab";
import { errorText } from "@utils/dayTargets.mjs";
import "@styles/styles-FixedValuesTab.css";

const { Title, Text } = Typography;

// dayjs' own "MMM" renders September as "Sep"; the header wants "Sept".
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];

const formatHeaderDate = (dateString: string) => {
    const date = dayjs(dateString);
    return `${SHORT_MONTHS[date.month()]} ${date.format("D, YYYY")}`;
};

const PLAN_TAB = "practice_plan";
const FIXED_TAB = "fixed_values";

// The tooltip of the tab that is off because the day already has the other method.
const LOCKED_TAB_TOOLTIPS: Record<string, string> = {
    [FIXED_TAB]: "This day already has saved fixed values. To build a practice plan, delete the fixed values first. You cannot get them back after you delete them.",
    [PLAN_TAB]: "This day already has a saved practice plan. To set fixed values, delete the practice plan first. You cannot get it back after you delete it.",
};

const SWITCH_PROMPTS: Record<string, { title: string; content: string; stay: string }> = {
    [PLAN_TAB]: {
        title: "Switch to fixed values?",
        content: "Your practice plan is not saved. If you switch, you lose the drills and settings you entered.",
        stay: "Stay on practice plan",
    },
    [FIXED_TAB]: {
        title: "Switch to practice plan?",
        content: "Your fixed values are not saved. If you switch, you lose the values you entered.",
        stay: "Stay on fixed values",
    },
};

export const PredictionModal = ({ visible, setVisibility, dateString, onLoad, onSuccess, drillData, showDayTargetTabs = false, method = null }: IPredictionModal) => {
    const [activeTab, setActiveTab] = useState<string>(method === FIXED_TAB ? FIXED_TAB : PLAN_TAB)
    const [fixedDirty, setFixedDirty] = useState(false)
    const [planBaseline, setPlanBaseline] = useState<string | null>(null)
    const [planLoaded, setPlanLoaded] = useState(false)
    const [step, setStep] = useState<number>(0)
    const [startDate, setStartDate] = useState<string>('')
    const [endDate, setEndDate] = useState<string>('')
    const [days, setDays] = useState<string[]>([])
    const [players, setPlayers] = useState<string[]>(['full-team'])
    const [playerLabels, setPlayerLabels] = useState<string[]>(['Full Team'])
    const [plan, setPlan] = useState<{ drill_id: number, duration: number }[]>([])
    const [drills, setDrills] = useState<{ drill_id: number, name: string }[]>([])
    const [costPerMin, setCostPerMin] = useState<any[]>([])
    const [submitLoading, setSubmitLoading] = useState(false)

    const games = useSelector(({ team }: any) => team.games)
    const opponent = games?.find(
        (game: { date: string, opponent?: string | null }) => game.date === dayjs(dateString).format("YYYY-MM-DD")
    )?.opponent

    useEffect(() => {
        onLoad?.()
    }, [])

    useEffect(() => {
        if (!visible || !dateString) return;
        if (showDayTargetTabs && method === FIXED_TAB) return;

        const fetchExistingPlan = async () => {
            try {
                const formattedDate = dayjs(dateString).format("YYYY-MM-DD");
                const existingPlan = await (trainingAPI as any).getPracticePlan(formattedDate, formattedDate);
                if (!existingPlan?.length) return;

                const { metadata } = existingPlan[0];

                setStartDate(dayjs(metadata.startDate) as any);
                setEndDate(dayjs(metadata.endDate) as any);
                setDays(metadata.days ?? []);
                setPlayers(metadata.players ?? ['full-team']);
                setPlayerLabels(metadata.playerLabels ?? ['Full Team']);
                setPlan(existingPlan.map(({ drill_id, duration }: { drill_id: number; duration: number }) => ({ drill_id, duration })));
                setStep(1);
            } catch {
                message.error("Failed to load existing practice plan");
            } finally {
                setPlanLoaded(true);
            }
        };

        fetchExistingPlan();
    }, [visible, dateString])

    // The practice plan tab has unsaved changes when its inputs differ from what it loaded.
    const planState = JSON.stringify({ startDate: String(startDate), endDate: String(endDate), days, players, plan })
    useEffect(() => {
        if (planLoaded && planBaseline === null) setPlanBaseline(planState)
    }, [planLoaded, planBaseline, planState])
    const planDirty = planBaseline !== null && planState !== planBaseline
    const activeTabDirty = activeTab === PLAN_TAB ? planDirty : fixedDirty

    const resetPlan = () => {
        setStep(0)
        setStartDate('')
        setEndDate('')
        setDays([])
        setPlayers(['full-team'])
        setPlayerLabels(['Full Team'])
        setPlan([])
        setPlanBaseline(null)
    }

    // Close with unsaved changes: ask first, as the RTP wizard does.
    const requestClose = () => {
        if (!showDayTargetTabs || !activeTabDirty) {
            setVisibility(false)
            return
        }
        Modal.confirm({
            centered: true,
            title: "Discard your changes?",
            content: "Leaving this page will discard your changes. Do you want to stay or discard?",
            okText: "Discard",
            okType: "danger",
            cancelText: "Stay",
            onOk: () => setVisibility(false),
        })
    }

    // Change tabs with unsaved changes: ask first; "Discard and switch" drops the tab's changes.
    const requestTabChange = (nextTab: string) => {
        if (nextTab === activeTab) return
        const switchTab = () => {
            if (activeTab === PLAN_TAB) resetPlan()
            setFixedDirty(false)
            setActiveTab(nextTab)
        }
        if (!activeTabDirty) {
            switchTab()
            return
        }
        const prompt = SWITCH_PROMPTS[activeTab]
        Modal.confirm({
            centered: true,
            title: prompt.title,
            content: prompt.content,
            okText: "Discard and switch",
            okType: "danger",
            cancelText: prompt.stay,
            onOk: switchTab,
        })
    }

    const handleSubmit = async () => {
        setSubmitLoading(true)
        try {
            await (trainingAPI as any).addPracticePlan(
                plan,
                dayjs(dateString).format("YYYY-MM-DD"),
                {
                    startDate: startDate,
                    endDate: endDate,
                    days: days,
                    players: players,
                    playerLabels: playerLabels,
                })
            message.success("Practice plan saved!")
            onSuccess?.()
            setVisibility(false)
        } catch (error) {
            console.error("Error adding practice plan", error)
            message.error(errorText(error, "Error adding practice plan"))
        } finally {
            setSubmitLoading(false)
        }
    }

    const stepData: {
        header: string;
        subHeader: string;
        component: ReactNode
        prevButton?: {
            text: string;
            logic: () => void
        },
        nextButton: {
            text: string;
            logic: () => void;
            disabled: boolean;
            tooltip?: string
        }
    }[] = [
            {
                header: `Create your practice plan (${formatHeaderDate(dateString)})${opponent ? ` (vs. ${opponent})` : ''}`,
                subHeader: "Target data: Please select some filters to narrow down your target data",
                component: <FirstStep
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    days={days}
                    setDays={setDays}
                    players={players}
                    setPlayers={setPlayers}
                    playerLabels={playerLabels}
                    setPlayerLabels={setPlayerLabels}
                />,
                nextButton: {
                    text: 'Next',
                    logic: () => setStep(step + 1),
                    disabled: (startDate === '' || endDate === '') || dayjs(endDate).diff(dayjs(startDate), 'years', true) > 2,
                    tooltip: "Date range needs to be selected and less than 2 years"
                }
            },
            {
                header: "Pick Drills",
                subHeader: "Build your practice plan by selecting your drills and durations",
                component: <SecondStep
                    startDate={startDate as any}
                    endDate={endDate as any}
                    days={days}
                    players={players}
                    playerLabels={playerLabels}
                    plan={plan}
                    setPlan={setPlan}
                    setDrills={setDrills}
                    setCostPerMin={setCostPerMin}
                    drillData={drillData}
                />,
                prevButton: {
                    text: "Previous",
                    logic: () => setStep(step - 1)
                },
                nextButton: {
                    text: 'Next',
                    logic: () => setStep(step + 1),
                    disabled: plan.length === 0,
                    tooltip: "Add at least one drill to the plan"
                }
            },
            {
                header: "Review & Apply",
                subHeader: "Review your practice plan before submitting",
                component: <ThirdStep plan={plan} drills={drills} costPerMin={costPerMin} />,
                prevButton: {
                    text: "Previous",
                    logic: () => setStep(step - 1)
                },
                nextButton: {
                    text: "Submit",
                    logic: () => handleSubmit(),
                    disabled: plan.length === 0,
                    tooltip: "Add at least one drill to the plan"
                }
            }
        ]

    // The tab that is off gets a tooltip that says why and what to do. A disabled element gets no
    // hover events, so the tooltip sits on a wrapper span.
    const tabLabel = (label: ReactNode, locked: boolean) => locked
        ? <Tooltip title={LOCKED_TAB_TOOLTIPS[method as string]}><span>{label}</span></Tooltip>
        : label

    const planBody = (
            <Row style={{ minHeight: '500px', maxHeight: '500px' }}>
                <Col span={6}>
                    <Steps
                        className="build-baseline-modal-steps"
                        direction="vertical"
                        current={step}
                        items={[
                            {
                                title: 'Create your practice plan',
                            },
                            {
                                title: 'Pick Drills',
                            },
                            {
                                title: 'Review & Apply',
                            },
                        ]}
                    />
                </Col>
                <Col span={18} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderLeft: 'solid lightgray 1px', paddingLeft: '30px' }}>
                    <Col>
                        <Col style={{ marginBottom: '20px' }}>
                            <Row>
                                <Title level={2} style={{ marginBottom: '0px' }}>{stepData[step].header}</Title>
                            </Row>
                            <Row>
                                <Text type="secondary">{stepData[step].subHeader}</Text>
                            </Row>
                        </Col>
                        {stepData[step].component}
                    </Col>
                    <Row style={{ justifyContent: "flex-end", columnGap: "20px" }}>
                        {stepData[step].prevButton &&
                            <Button type="default" onClick={stepData[step].prevButton.logic}>{stepData[step].prevButton.text}</Button>
                        }
                        {stepData[step].nextButton &&
                            <Tooltip title={(stepData[step].nextButton.tooltip && stepData[step].nextButton.disabled) && stepData[step].nextButton.tooltip}>
                                <Button
                                    type="primary"
                                    onClick={stepData[step].nextButton.logic}
                                    disabled={stepData[step].nextButton.disabled || submitLoading}
                                    loading={step === 2 && submitLoading}
                                >
                                    {stepData[step].nextButton.text}
                                </Button>
                            </Tooltip>
                        }
                    </Row>
                </Col>
            </Row>
    )

    return (
        <Modal open={visible} onCancel={requestClose} footer={null} width={1200} bodyStyle={{ minHeight: '500px' }}>
            {showDayTargetTabs ? (
                <Tabs
                    activeKey={activeTab}
                    onChange={requestTabChange}
                    destroyInactiveTabPane
                    items={[
                        {
                            key: PLAN_TAB,
                            label: tabLabel("Practice plan", method === FIXED_TAB),
                            disabled: method === FIXED_TAB,
                            children: planBody,
                        },
                        {
                            key: FIXED_TAB,
                            label: tabLabel(<>Fixed values <Tag color="blue" className="day-targets-new-badge">NEW</Tag></>, method === PLAN_TAB),
                            disabled: method === PLAN_TAB,
                            children: (
                                <FixedValuesTab
                                    dateString={dateString}
                                    mode={method === FIXED_TAB ? "edit" : "create"}
                                    onDirtyChange={setFixedDirty}
                                    onSaved={() => { onSuccess?.(); setVisibility(false) }}
                                    onDeleted={() => { onSuccess?.(); setVisibility(false) }}
                                    onCancel={requestClose}
                                />
                            ),
                        },
                    ]}
                />
            ) : planBody}
        </Modal>
    )
}

interface IPredictionModal {
    visible: boolean;
    setVisibility: React.Dispatch<boolean>,
    dateString: string,
    onLoad?: () => void,
    onSuccess?: () => void,
    drillData: { drill_id: number, team_id_id: number, latest_date: string, name: string }[],
    // Day targets: the tabs show only for a caller that knows the day's method. Without it (the
    // overview version 1 calendar), the dialog is the practice plan builder.
    showDayTargetTabs?: boolean,
    // The saved method of the day, which locks the other tab. null: a new day with both tabs on.
    method?: "practice_plan" | "fixed_values" | null
}

export default PredictionModal;
